import styled from "styled-components";

export const EditorPage = styled.div`
  min-height: 100vh;
`;

export const EditorLayout = styled.main`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 56px;
  max-width: 980px;
  margin: 0 auto;
  padding: 72px 24px 110px;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    max-width: 320px;
    padding: 56px 0px 88px;
  }
`;

export const EditorMain = styled.section``;

export const EditorKicker = styled.p`
  margin: 0px 0px 10px;
  color: var(--muted);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const EditorHeader = styled.div`
  display: flex;
  gap: 24px;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 48px;
`;

export const EditorTitle = styled.h1`
  margin: 0px;
  font-weight: normal;
`;

export const EditorNewButton = styled.button`
  flex: 0 0 auto;
  padding: 0px;
  border: none;
  color: var(--subtle);
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
  text-underline-offset: 4px;

  &:hover {
    color: var(--heading);
  }

  &:focus-visible {
    outline: 2px solid var(--text);
    outline-offset: 4px;
  }
`;

export const EditorField = styled.label`
  display: block;
  margin-bottom: 32px;
  color: var(--subtle);
  span {
    display: block;
    margin-bottom: 8px;
  }
`;

export const EditorInput = styled.input`
  width: 100%;
  padding: 10px 0px;
  border: none;
  border-bottom: 1px solid var(--line);
  border-radius: 0px;
  outline: none;
  color: var(--text);
  background: transparent;
  font-family: "Literata", Georgia, serif;
  font-size: 22px;
  &:focus {
    border-color: var(--text);
  }
`;

export const EditorTextarea = styled.textarea`
  width: 100%;
  min-height: 360px;
  padding: 18px 0px;
  border: none;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  border-radius: 0px;
  outline: none;
  resize: vertical;
  color: var(--text);
  background: transparent;
  font-family: "Literata", Georgia, serif;
  font-size: 19px;
  line-height: 34px;
  &:focus {
    border-color: var(--text);
  }
`;

export const EditorFileInput = styled.input`
  display: block;
  width: 100%;
  padding: 12px 0px;
  color: var(--subtle);
  font-size: 14px;
`;

export const EditorImagePreview = styled.img`
  display: block;
  width: min(100%, 520px);
  max-height: 360px;
  margin-top: 16px;
  object-fit: cover;
  border-radius: 4px;
`;

export const EditorRemoveImage = styled.button`
  margin-top: 10px;
  padding: 0px;
  border: none;
  color: var(--subtle);
  background: transparent;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

export const EditorSidebar = styled.aside`
  padding-left: 28px;
  border-left: 1px solid var(--line);
  @media (max-width: 768px) {
    padding: 28px 0px 0px;
    border-top: 1px solid var(--line);
    border-left: none;
  }
`;

export const EditorSidebarTitle = styled.h2`
  margin: 0px 0px 12px;
  font-weight: normal;
`;

export const EditorHelp = styled.p`
  color: var(--muted);
  font-size: 14px;
  line-height: 22px;
`;

export const EditorButton = styled.button<{ primary?: boolean }>`
  width: 100%;
  margin-top: 10px;
  padding: 11px 16px;
  border: 1px solid var(--text);
  border-radius: 24px;
  cursor: pointer;
  color: ${({ primary }) => (primary ? "var(--page)" : "var(--text)")};
  background: ${({ primary }) => (primary ? "var(--text)" : "transparent")};
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.78;
  }
  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }
`;

export const EditorDeleteButton = styled.button`
  width: 100%;
  margin-top: 14px;
  padding: 4px 0px;
  border: none;
  color: #8b4a43;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: #692f2a;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }
`;

export const EditorStatus = styled.p<{ error?: boolean }>`
  min-height: 22px;
  color: ${({ error }) => (error ? "#d98277" : "var(--subtle)")};
  font-size: 14px;
  line-height: 22px;
`;

export const EditorLibrary = styled.section`
  margin-top: 38px;
  padding-top: 30px;
  border-top: 1px solid var(--line);
`;

export const EditorLibraryGroup = styled.div`
  margin-top: 26px;
`;

export const EditorLibraryHeading = styled.h3`
  margin: 0px 0px 8px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const EditorLibraryItem = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  margin: 0px;
  padding: 10px 8px;
  border: none;
  border-radius: 5px;
  color: var(--text);
  background: ${({ $active }) => ($active ? "var(--surface)" : "transparent")};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: var(--surface);
  }

  &:focus-visible {
    outline: 2px solid var(--text);
    outline-offset: 1px;
  }

  strong,
  span {
    display: block;
  }

  strong {
    overflow: hidden;
    font-family: "Literata", Georgia, serif;
    font-size: 14px;
    font-weight: 450;
    line-height: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    margin-top: 2px;
    color: var(--muted);
    font-size: 11px;
    line-height: 16px;
  }
`;

export const EditorLibraryEmpty = styled.p`
  margin: 8px 0px;
  color: var(--muted);
  font-size: 12px;
  line-height: 18px;
`;
